import React, { useState, useEffect } from 'react';
import './BookRooms.css';

const BookRooms = ({ onClose, user, selectedRoomFilter }) => {
    console.log('BookRooms component rendering with props:', { onClose, user, selectedRoomFilter });
    
    const [availableRooms, setAvailableRooms] = useState([]);
    const [roomCatalog, setRoomCatalog] = useState({}); // Preserve room metadata even when temporarily unavailable
    const [dynamicPricing, setDynamicPricing] = useState({}); // Store dynamic pricing by room and date
    const [loading, setLoading] = useState(true);
    
    const [bookingData, setBookingData] = useState({
        checkInDate: '',
        checkOutDate: '',
        customerName: '',
        mobileNumber: '',
        email: '',
        selectedRooms: {}, // Will be populated with roomId: { occupancy, count }
        extraAdults: 0,
        extraChildren: 0,
        withoutBreakfast: false
    });

    const [currentStep, setCurrentStep] = useState('booking'); // 'booking', 'bill', 'payment', 'success' (otp removed)
    // const [otp, setOtp] = useState(''); // UNUSED: OTP not needed with guest authentication
    const [authToken, setAuthToken] = useState('');
    const [errors, setErrors] = useState({});
    const [bookingResult, setBookingResult] = useState(null); // Store booking result for success popup
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [recoverableBookings, setRecoverableBookings] = useState(null);
    const [recoverablePaymentError, setRecoverablePaymentError] = useState('');
    
    // Resend limit tracking
    const [smsResendCount, setSmsResendCount] = useState(0);
    const [emailResendCount, setEmailResendCount] = useState(0);
    const [showResendLimitPopup, setShowResendLimitPopup] = useState(false);
    const [resendLimitMessage, setResendLimitMessage] = useState('');

    const mergeSelectedRooms = (prevSelectedRooms, rooms) => {
        const merged = { ...prevSelectedRooms };
        rooms.forEach(room => {
            const key = String(room.id);
            if (!merged[key]) {
                merged[key] = {
                    occupancy: '',
                    count: 1
                };
            }
        });
        return merged;
    };

    const getRoomDisplayPriority = (room) => {
        const roomName = (room?.name || '').toUpperCase();

        if (roomName.includes('SUPER DELUXE')) return 2;
        if (roomName.includes('DELUXE')) return 1;
        if (roomName.includes('EXECUTIVE')) return 3;
        if (roomName.includes('PREMIERE') || roomName.includes('PREMIER')) return 4;

        return 99;
    };

    const sortRoomsByDisplayOrder = (rooms) => {
        return [...rooms].sort((a, b) => {
            const priorityDiff = getRoomDisplayPriority(a) - getRoomDisplayPriority(b);
            if (priorityDiff !== 0) {
                return priorityDiff;
            }

            return (a?.name || '').localeCompare(b?.name || '');
        });
    };

    const extraCharges = {
        adult: 700,
        child: 700,
        breakfastDiscount: 100
    };

    const getTotalGuestsCount = () => {
        let totalGuests = 0;

        Object.entries(bookingData.selectedRooms).forEach(([, roomSelection]) => {
            if (roomSelection.occupancy && roomSelection.count > 0) {
                totalGuests += roomSelection.count * (roomSelection.occupancy === 'double' ? 2 : 1);
            }
        });

        return totalGuests + bookingData.extraAdults + bookingData.extraChildren;
    };

    const getBreakfastDiscountAmount = () => {
        if (!bookingData.withoutBreakfast) {
            return 0;
        }

        return getTotalGuestsCount() * extraCharges.breakfastDiscount;
    };

    // Load available rooms from backend
    useEffect(() => {
        loadAvailableRooms();
    }, [bookingData.checkInDate, bookingData.checkOutDate]);

    // Google Hotel Center: Handle deep link URL parameters
    useEffect(() => {
        // Only process once, when rooms are first loaded
        if (availableRooms.length === 0) return;

        const urlParams = new URLSearchParams(window.location.search);
        const roomParam = urlParams.get('room');        // e.g., "deluxe-room"
        const dateParam = urlParams.get('date');        // e.g., "2026-07-20"
        const occupancyParam = urlParams.get('occupancy'); // e.g., "single" or "double"
        const breakfastParam = urlParams.get('breakfast'); // e.g., "1" or "0"

        // If any Google Hotel parameters exist, pre-fill the form
        if (roomParam || dateParam || occupancyParam) {
            console.log('🏨 Google Hotel Center booking detected:', {
                room: roomParam,
                date: dateParam,
                occupancy: occupancyParam,
                breakfast: breakfastParam
            });

            const updates = {};

            // Pre-fill check-in date
            if (dateParam) {
                const checkInDate = dateParam;
                const checkOutDate = new Date(dateParam);
                checkOutDate.setDate(checkOutDate.getDate() + 1); // Default to 1 night
                
                updates.checkInDate = checkInDate;
                updates.checkOutDate = checkOutDate.toISOString().split('T')[0];
            }

            // Pre-fill breakfast preference
            if (breakfastParam !== null) {
                updates.withoutBreakfast = breakfastParam === '0';
            }

            // Find and pre-select the room
            if (roomParam) {
                // Map Google room ID to actual room
                // Google: "deluxe-room" → DB: room with type/name containing "deluxe"
                const searchTerm = roomParam.toLowerCase().replace(/-/g, ' '); // "deluxe room"
                
                const matchedRoom = availableRooms.find(room => {
                    const roomType = (room.type || '').toLowerCase();
                    const roomName = (room.name || '').toLowerCase();
                    return roomType.includes(searchTerm) || roomName.includes(searchTerm);
                });

                if (matchedRoom) {
                    console.log('✅ Auto-selected room from Google Hotel:', matchedRoom.name);
                    
                    const occupancy = occupancyParam === 'single' ? 'single' : 'double';
                    
                    updates.selectedRooms = {
                        ...bookingData.selectedRooms,
                        [String(matchedRoom.id)]: {
                            occupancy: occupancy,
                            count: 1
                        }
                    };
                } else {
                    console.warn('⚠️  Room not found for Google ID:', roomParam);
                }
            }

            // Apply all updates at once
            if (Object.keys(updates).length > 0) {
                setBookingData(prev => ({ ...prev, ...updates }));
                
                // Optional: Scroll to booking form section
                setTimeout(() => {
                    const formElement = document.querySelector('.book-rooms-modal');
                    if (formElement) {
                        formElement.scrollIntoView({ behavior: 'smooth', block: 'start' });
                    }
                }, 500);
            }
        }
    }, [availableRooms]); // Run when rooms are loaded

    const loadAvailableRooms = async () => {
        try {
            setLoading(true);
            let availabilityUrl = '/api/rooms/available';
            if (bookingData.checkInDate && bookingData.checkOutDate) {
                const params = new URLSearchParams({
                    checkIn: bookingData.checkInDate,
                    checkOut: bookingData.checkOutDate
                });
                availabilityUrl = `/api/rooms/available?${params.toString()}`;
            }

            const response = await fetch(availabilityUrl);
            if (response.ok) {
                const rooms = await response.json();
                const roomsWithPricing = await Promise.all(
                    rooms.map(async (room) => {
                        try {
                            const pricingResponse = await fetch(
                                `/api/public/pricing/check?roomId=${room.id}&checkIn=${bookingData.checkInDate}&checkOut=${bookingData.checkOutDate}`,
                                {
                                    method: 'GET',
                                    headers: {
                                        'Content-Type': 'application/json'
                                    }
                                }
                            );

                            if (!pricingResponse.ok) {
                                return room;
                            }

                            const roomPricing = await pricingResponse.json();
                            if (roomPricing?.customPricing) {
                                return {
                                    ...room,
                                    singleOccupancyPrice: roomPricing.singleOccupancyPrice,
                                    doubleOccupancyPrice: roomPricing.doubleOccupancyPrice,
                                    isDynamicPricing: true,
                                    dynamicPricingNote: roomPricing.notes
                                };
                            }

                            return room;
                        } catch (error) {
                            console.error(`❌ Error loading pricing for room ${room.id}:`, error);
                            return room;
                        }
                    })
                );

                setAvailableRooms(roomsWithPricing);
                setRoomCatalog(prev => {
                    const next = { ...prev };
                    roomsWithPricing.forEach(room => {
                        next[String(room.id)] = room;
                    });
                    return next;
                });
                
                setBookingData(prev => ({
                    ...prev,
                    // Keep previous selections, but always cap room count to latest availability for selected dates.
                    selectedRooms: Object.fromEntries(
                        Object.entries(mergeSelectedRooms(prev.selectedRooms, roomsWithPricing)).map(([roomId, selection]) => {
                            const room = roomsWithPricing.find(r => String(r.id) === String(roomId));
                            if (room && room.availableRooms > 0) {
                                return [
                                    roomId,
                                    {
                                        ...selection,
                                        count: Math.min(selection.count || 1, room.availableRooms)
                                    }
                                ];
                            }

                            return [
                                roomId,
                                {
                                    occupancy: '',
                                    count: 0
                                }
                            ];
                        })
                    )
                }));
            } else {
                console.error('Error loading rooms:', response.statusText);
            }
        } catch (error) {
            console.error('Error loading rooms:', error);
        } finally {
            setLoading(false);
        }
    };

    const handleInputChange = (e) => {
        const { name, value, type, checked } = e.target;
        
        if (type === 'checkbox') {
            setBookingData(prev => ({
                ...prev,
                [name]: checked
            }));
        } else {
            setBookingData(prev => ({
                ...prev,
                [name]: value
            }));
            
            // Clear error when user starts typing
            if (errors[name]) {
                setErrors(prev => ({
                    ...prev,
                    [name]: ''
                }));
            }
            
            // Handle date changes and fetch available rooms
            if (name === 'checkInDate' || name === 'checkOutDate') {
                // Validate dates
                if (name === 'checkInDate' && bookingData.checkOutDate) {
                    if (new Date(value) >= new Date(bookingData.checkOutDate)) {
                        setErrors(prev => ({
                            ...prev,
                            checkOutDate: 'Check-out date must be after check-in date'
                        }));
                        return;
                    } else {
                        setErrors(prev => ({
                            ...prev,
                            checkOutDate: ''
                        }));
                    }
                }
                
                if (name === 'checkOutDate' && bookingData.checkInDate) {
                    if (new Date(bookingData.checkInDate) >= new Date(value)) {
                        setErrors(prev => ({
                            ...prev,
                            checkOutDate: 'Check-out date must be after check-in date'
                        }));
                        return;
                    } else {
                        setErrors(prev => ({
                            ...prev,
                            checkOutDate: ''
                        }));
                    }
                }
                
                // Date-driven availability is loaded by useEffect, avoid duplicate fetches/races here.
            }
        }
    };

    const handleRoomOccupancyChange = (roomId, occupancy) => {
        setBookingData(prev => ({
            ...prev,
            selectedRooms: {
                ...prev.selectedRooms,
                [roomId]: {
                    ...prev.selectedRooms[roomId],
                    occupancy: prev.selectedRooms[roomId]?.occupancy === occupancy ? '' : occupancy
                }
            }
        }));
    };

    const handleRoomCountChange = (roomId, value) => {
        const count = Math.max(1, Math.min(5, parseInt(value) || 1));
        setBookingData(prev => ({
            ...prev,
            selectedRooms: {
                ...prev.selectedRooms,
                [roomId]: {
                    ...prev.selectedRooms[roomId],
                    count: count
                }
            }
        }));
    };

    const handleExtraCountChange = (field, value) => {
        const count = Math.max(0, Math.min(5, parseInt(value) || 0));
        setBookingData(prev => ({
            ...prev,
            [field]: count
        }));
    };

    const validateForm = () => {
        const newErrors = {};
        
        // Name validation
        if (!bookingData.customerName.trim()) {
            newErrors.customerName = 'Full name is required';
        } else if (bookingData.customerName.trim().length < 2) {
            newErrors.customerName = 'Name must be at least 2 characters long';
        } else if (!/^[a-zA-Z\s]+$/.test(bookingData.customerName.trim())) {
            newErrors.customerName = 'Name should only contain letters and spaces';
        }
        
        // Mobile number validation
        if (!bookingData.mobileNumber.trim()) {
            newErrors.mobileNumber = 'Mobile number is required';
        } else if (!/^[6-9]\d{9}$/.test(bookingData.mobileNumber.trim())) {
            newErrors.mobileNumber = 'Please enter a valid 10-digit mobile number';
        }
        
        // Email validation
        if (!bookingData.email.trim()) {
            newErrors.email = 'Email address is required';
        } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(bookingData.email.trim())) {
            newErrors.email = 'Please enter a valid email address';
        }
        
        // Date validation
        if (!bookingData.checkInDate) {
            newErrors.checkInDate = 'Check-in date is required';
        } else {
            const checkInDate = new Date(bookingData.checkInDate);
            const today = new Date();
            today.setHours(0, 0, 0, 0);
            
            if (checkInDate < today) {
                newErrors.checkInDate = 'Check-in date cannot be in the past';
            }
        }
        
        if (!bookingData.checkOutDate) {
            newErrors.checkOutDate = 'Check-out date is required';
        } else if (bookingData.checkInDate && bookingData.checkOutDate) {
            const checkInDate = new Date(bookingData.checkInDate);
            const checkOutDate = new Date(bookingData.checkOutDate);
            
            if (checkOutDate <= checkInDate) {
                newErrors.checkOutDate = 'Check-out date must be after check-in date';
            }
        }
        
        // Room selection validation
        const hasValidRoom = Object.values(bookingData.selectedRooms).some(room => 
            room.occupancy && room.count > 0
        );
        
        if (!hasValidRoom) {
            newErrors.rooms = 'Please select at least one room with occupancy type';
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleBookNow = async () => {
        if (!validateForm()) return;

        // Clear any existing OTP when starting new booking flow (not needed with guest auth)
        // setOtp('');
        
        // Use guest authentication instead of OTP during DLT registration period
        try {
            console.log('� Starting guest authentication for phone:', bookingData.mobileNumber);
            
            const guestAuthPayload = {
                phone: bookingData.mobileNumber,
                guestMode: true
            };
            
            const response = await fetch('/api/auth/guest-auth', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify(guestAuthPayload),
            });

            if (response.ok) {
                const data = await response.json();
                if (data.success && data.token) {
                    setAuthToken(data.token);
                    console.log('✅ Guest authentication successful - proceeding to booking');
                    setCurrentStep('bill');
                } else {
                    console.error('❌ Guest authentication failed:', data.message);
                    alert('Authentication failed: ' + (data.message || 'Unknown error'));
                }
            } else {
                console.error('❌ Guest authentication request failed with status:', response.status);
                alert('Authentication failed. Please check your phone number and try again.');
            }
        } catch (error) {
            console.error('❌ Guest authentication error:', error);
            alert('Authentication error. Please check your connection and try again.');
        }
        
        /* 
        // Original OTP flow - commented out temporarily
        // Clear any existing OTP when starting new booking flow
        setOtp('');

        // Send OTP using existing backend API
        try {
            const response = await fetch('/api/auth/send-otp', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    phone: bookingData.mobileNumber
                }),
            });

            if (response.ok) {
                setCurrentStep('otp');
            } else {
                const errorData = await response.json();
                alert(errorData.message || 'Error sending OTP. Please try again.');
            }
        } catch (error) {
            console.error('Error sending OTP:', error);
            alert('Error sending OTP. Please try again.');
        }
        */
    };

    // UNUSED: handleOtpVerification - now using guest authentication instead
    /*const handleOtpVerification = async () => {
        if (!otp || otp.trim().length === 0) {
            alert('Please enter the OTP');
            return;
        }

        try {
            const response = await fetch('/api/auth/verify-otp', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    phone: bookingData.mobileNumber,
                    otp: otp.trim()
                }),
            });

            if (response.ok) {
                const responseData = await response.json();
                
                // Store the JWT token if present
                if (responseData.token) {
                    setAuthToken(responseData.token);
                    console.log('JWT token received and stored');
                }
                
                setCurrentStep('bill');
            } else {
                const errorData = await response.json();
                const errorMessage = errorData.message || 'Invalid OTP. Please try again.';
                
                // Check if OTP is expired or invalid
                if (errorMessage.toLowerCase().includes('expired') || 
                    errorMessage.toLowerCase().includes('expire') ||
                    errorMessage.toLowerCase().includes('timeout')) {
                    
                    if (window.confirm('OTP has expired. Would you like to regenerate a new OTP?')) {
                        await handleRegenerateOtp();
                    }
                } else if (errorMessage.toLowerCase().includes('invalid') || 
                          errorMessage.toLowerCase().includes('incorrect') ||
                          errorMessage.toLowerCase().includes('wrong')) {
                    
                    const shouldRegenerate = window.confirm('Invalid OTP. Would you like to:\nOK - Try entering OTP again\nCancel - Regenerate new OTP');
                    if (!shouldRegenerate) {
                        await handleRegenerateOtp();
                    } else {
                        // Clear current OTP to let user try again
                        setOtp('');
                    }
                } else {
                    alert(errorMessage);
                }
            }
        } catch (error) {
            console.error('OTP verification error:', error);
            alert('Error verifying OTP. Please check your internet connection and try again.');
        }
    };

    const handleRegenerateOtp = async () => {
        try {
            // Clear existing OTP
            setOtp('');
            
            const response = await fetch('/api/auth/send-otp', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    phone: bookingData.mobileNumber
                }),
            });

            if (response.ok) {
                alert('New OTP sent successfully!');
                // Stay on OTP step
            } else {
                const errorData = await response.json();
                alert(errorData.message || 'Error sending new OTP. Please try again.');
            }
        } catch (error) {
            console.error('Error regenerating OTP:', error);
            alert('Error sending new OTP. Please try again.');
        }
    };*/

    const handleGoBackToBooking = () => {
        // Clear OTP and any errors when going back to booking page (OTP not used with guest auth)
        // setOtp('');
        setErrors({});
        setCurrentStep('booking');
    };

    // UNUSED: handleGoBackToOtp - not needed with guest authentication
    /*const handleGoBackToOtp = () => {
        // Clear OTP when going back from bill to OTP
        setOtp('');
        setCurrentStep('otp');
    };*/

    // Helper function to create consolidated notification payload for resend operations
    const createConsolidatedPayload = (paymentType = 'pay-at-hotel') => {
        // Calculate total amount using the same logic as UI calculateTotal()
        const totalAmount = calculateTotal();
        
        // Calculate total guests using the same logic as UI
        let totalGuests = 0;
        Object.entries(bookingData.selectedRooms).forEach(([roomId, roomSelection]) => {
            if (roomSelection.occupancy && roomSelection.count > 0) {
                totalGuests += roomSelection.count * (roomSelection.occupancy === 'double' ? 2 : 1);
            }
        });
        totalGuests += bookingData.extraAdults + bookingData.extraChildren;
        
        return {
            phoneNumber: bookingData.mobileNumber,
            guestName: bookingData.customerName,
            guestEmail: bookingData.email,
            totalAmount: totalAmount.toString(),
            totalGuests: totalGuests,
            paymentMethod: paymentType,
            bookings: bookingResult.bookings.map(booking => ({
                bookingId: booking.id || booking.bookingId,
                roomName: booking.roomName || 'Room',
                checkInDate: formatDateForBackend(bookingData.checkInDate),
                checkOutDate: formatDateForBackend(bookingData.checkOutDate),
                roomAmount: (booking.totalPrice || 0).toString(),
                guests: booking.guests || 1,
                breakfast: booking.breakfast || false,
                breakfastCount: booking.breakfastCount || 0,
                extraBed: booking.extraBed || false,
                extraBedCount: booking.extraBedCount || 0,
                occupancyType: booking.occupancyType || 'SINGLE'
            }))
        };
    };

    // Resend confirmation functions
    const handleResendSMS = async () => {
        // Check if SMS resend limit reached
        if (smsResendCount >= 2) {
            setResendLimitMessage('SMS resend limit reached (2/2). Please contact the hotel directly for booking confirmation at +917796775959.');
            setShowResendLimitPopup(true);
            return;
        }

        try {
            // Use consolidated payload with all booking details
            const consolidatedPayload = createConsolidatedPayload(bookingResult.paymentType === 'without_payment' ? 'pay-at-hotel' : 'online');
            
            const response = await fetch('/api/notifications/resend-sms', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    ...(authToken && { 'Authorization': `Bearer ${authToken}` })
                },
                body: JSON.stringify(consolidatedPayload),
            });

            if (response.ok) {
                setSmsResendCount(prev => prev + 1);
                const newCount = smsResendCount + 1;
                if (newCount >= 2) {
                    alert(`SMS confirmation sent successfully! (${newCount}/2 - Limit reached)`);
                } else {
                    alert(`SMS confirmation sent successfully! (${newCount}/2)`);
                }
            } else {
                alert('Failed to send SMS confirmation. Please try again.');
            }
        } catch (error) {
            console.error('Error resending SMS:', error);
            alert('Error sending SMS confirmation. Please try again.');
        }
    };

    const handleResendEmail = async () => {
        // Check if Email resend limit reached
        if (emailResendCount >= 2) {
            setResendLimitMessage('Email resend limit reached (2/2). Please contact the hotel directly for booking confirmation at +917796775959.');
            setShowResendLimitPopup(true);
            return;
        }

        try {
            // Use consolidated payload with all booking details
            const consolidatedPayload = createConsolidatedPayload(bookingResult.paymentType === 'without_payment' ? 'pay-at-hotel' : 'online');
            
            const response = await fetch('/api/notifications/resend-email', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    ...(authToken && { 'Authorization': `Bearer ${authToken}` })
                },
                body: JSON.stringify(consolidatedPayload),
            });

            if (response.ok) {
                setEmailResendCount(prev => prev + 1);
                const newCount = emailResendCount + 1;
                if (newCount >= 2) {
                    alert(`Email confirmation sent successfully! (${newCount}/2 - Limit reached)`);
                } else {
                    alert(`Email confirmation sent successfully! (${newCount}/2)`);
                }
            } else {
                alert('Failed to send email confirmation. Please try again.');
            }
        } catch (error) {
            console.error('Error resending email:', error);
            alert('Error sending email confirmation. Please try again.');
        }
    };

    const handleCloseSuccess = () => {
        setCurrentStep('booking');
        setBookingResult(null);
        // Reset resend counters when closing success popup
        setSmsResendCount(0);
        setEmailResendCount(0);
        setShowResendLimitPopup(false);
        onClose();
    };

    const getRoomsForSelection = () => {
        const roomsMap = new Map();

        availableRooms.forEach(room => {
            roomsMap.set(String(room.id), room);
        });

        Object.entries(bookingData.selectedRooms).forEach(([roomId, roomSelection]) => {
            if (!roomSelection?.occupancy || roomSelection.count <= 0) {
                return;
            }

            if (!roomsMap.has(roomId) && roomCatalog[roomId]) {
                roomsMap.set(roomId, {
                    ...roomCatalog[roomId],
                    availableRooms: 0,
                    __preservedSelection: true
                });
            }
        });

        return sortRoomsByDisplayOrder(Array.from(roomsMap.values()));
    };

    const calculateTotal = () => {
        let subtotal = 0;
        
        // Calculate number of nights
        const checkInDate = new Date(bookingData.checkInDate);
        const checkOutDate = new Date(bookingData.checkOutDate);
        const nights = Math.ceil((checkOutDate - checkInDate) / (1000 * 60 * 60 * 24));
        
        // Room charges using dynamic pricing when available, fallback to base prices
        Object.entries(bookingData.selectedRooms).forEach(([roomId, roomSelection]) => {
            if (roomSelection.occupancy && roomSelection.count > 0) {
                const room = availableRooms.find(r => r.id === parseInt(roomId)) || roomCatalog[roomId];
                if (room) {
                    // Calculate pricing for each night with dynamic pricing
                    let totalRoomCost = 0;
                    const startDate = new Date(bookingData.checkInDate);
                    const endDate = new Date(bookingData.checkOutDate);
                    const currentDate = new Date(startDate);
                    
                    while (currentDate < endDate) {
                        const dateStr = currentDate.toISOString().split('T')[0];
                        let dayPrice;
                        
                        // Check if we have dynamic pricing for this room and date
                        const roomDynamicPricing = dynamicPricing[roomId];
                        const dayPricing = roomDynamicPricing?.find(p => p.date === dateStr);
                        
                        if (dayPricing) {
                            // Use dynamic pricing
                            dayPrice = roomSelection.occupancy === 'single' 
                                ? dayPricing.singlePrice 
                                : dayPricing.doublePrice;
                        } else {
                            // Use base room pricing
                            dayPrice = roomSelection.occupancy === 'single' 
                                ? room.singleOccupancyPrice 
                                : room.doubleOccupancyPrice;
                        }
                        
                        totalRoomCost += dayPrice * roomSelection.count;
                        currentDate.setDate(currentDate.getDate() + 1);
                    }
                    
                    subtotal += totalRoomCost;
                }
            }
        });
        
        // Extra charges (per night)
        subtotal += (bookingData.extraAdults * extraCharges.adult) * nights;
        subtotal += (bookingData.extraChildren * extraCharges.child) * nights;
        
        subtotal -= getBreakfastDiscountAmount();
        
        // Add 5% GST to subtotal
        const gstAmount = subtotal * 0.05;
        const totalWithGst = subtotal + gstAmount;
        
        return Math.round(totalWithGst); // Round to nearest rupee
    };

    // Helper function to get calculation breakdown for display
    const getCalculationBreakdown = () => {
        let subtotal = 0;
        
        // Calculate number of nights
        const checkInDate = new Date(bookingData.checkInDate);
        const checkOutDate = new Date(bookingData.checkOutDate);
        const nights = Math.ceil((checkOutDate - checkInDate) / (1000 * 60 * 60 * 24));
        
        // Room charges using dynamic pricing when available, fallback to base prices
        Object.entries(bookingData.selectedRooms).forEach(([roomId, roomSelection]) => {
            if (roomSelection.occupancy && roomSelection.count > 0) {
                const room = availableRooms.find(r => r.id === parseInt(roomId)) || roomCatalog[roomId];
                if (room) {
                    // Calculate pricing for each night with dynamic pricing
                    let totalRoomCost = 0;
                    const startDate = new Date(bookingData.checkInDate);
                    const endDate = new Date(bookingData.checkOutDate);
                    const currentDate = new Date(startDate);
                    
                    while (currentDate < endDate) {
                        const dateStr = currentDate.toISOString().split('T')[0];
                        let dayPrice;
                        
                        // Check if we have dynamic pricing for this room and date
                        const roomDynamicPricing = dynamicPricing[roomId];
                        const dayPricing = roomDynamicPricing?.find(p => p.date === dateStr);
                        
                        if (dayPricing) {
                            // Use dynamic pricing
                            dayPrice = roomSelection.occupancy === 'single' 
                                ? dayPricing.singlePrice 
                                : dayPricing.doublePrice;
                        } else {
                            // Use base room pricing
                            dayPrice = roomSelection.occupancy === 'single' 
                                ? room.singleOccupancyPrice 
                                : room.doubleOccupancyPrice;
                        }
                        
                        totalRoomCost += dayPrice * roomSelection.count;
                        currentDate.setDate(currentDate.getDate() + 1);
                    }
                    
                    subtotal += totalRoomCost;
                }
            }
        });
        
        // Extra charges (per night)
        subtotal += (bookingData.extraAdults * extraCharges.adult) * nights;
        subtotal += (bookingData.extraChildren * extraCharges.child) * nights;
        
        const breakfastDiscount = getBreakfastDiscountAmount();
        subtotal -= breakfastDiscount;
        
        // Calculate GST and total
        const gstAmount = subtotal * 0.05;
        const totalWithGst = subtotal + gstAmount;
        
        return {
            subtotal: Math.round(subtotal),
            breakfastDiscount: Math.round(breakfastDiscount),
            gstAmount: Math.round(gstAmount),
            total: Math.round(totalWithGst)
        };
    };

    const handleBookWithoutPayment = async () => {
        if (isSubmitting) return;
        try {
            setIsSubmitting(true);
            // Reuse already-created bookings from failed online attempt to avoid duplicate booking creation.
            const bookings = recoverableBookings || await saveBookingForPayment('PAY_AT_HOTEL');
            
            // Send consolidated notification
            await sendConsolidatedNotification(bookings, 'pay-at-hotel');
            
            // Show success page
            const bookingIds = bookings.map(b => b.id || b.bookingId);
            setBookingResult({
                success: true,
                paymentType: 'without_payment',
                bookingIds: bookingIds,
                totalAmount: calculateTotal(),
                guestName: bookingData.customerName,
                guestEmail: bookingData.email,
                guestPhone: bookingData.mobileNumber,
                bookings: bookings
            });
            setRecoverableBookings(null);
            setRecoverablePaymentError('');
            setCurrentStep('success');
        } catch (error) {
            console.error('Error in Book without payment flow:', error);
            alert('Error saving booking. Please try again.');
        } finally {
            setIsSubmitting(false);
        }
    };

    const handlePayOnline = async () => {
        if (isSubmitting) return;
        try {
            setIsSubmitting(true);
            // Reuse existing bookings when retrying after cancellation/failure.
            const bookings = recoverableBookings || await saveBookingForPayment('ONLINE');
            setRecoverableBookings(bookings);
            setRecoverablePaymentError('');
            
            // Then initiate payment
            await initiateRazorpayPayment(bookings);
        } catch (error) {
            console.error('Error in Pay Online flow:', error);
            setRecoverablePaymentError(error?.message || 'Payment failed. You can retry online or complete booking without payment.');
            alert((error?.message || 'Payment failed.') + ' You can retry online or book without payment.');
        } finally {
            setIsSubmitting(false);
        }
    };

    // Helper function to get API URL
    const getApiUrl = (endpoint) => {
        // Use relative path for same domain or full URL for different domains
        return endpoint;
    };

    // Load Razorpay script
    const loadRazorpayScript = () => {
        return new Promise((resolve) => {
            // Check if Razorpay is already loaded
            if (window.Razorpay) {
                console.log('✅ Razorpay already loaded');
                resolve(true);
                return;
            }

            // Check if script is already being loaded
            const existingScript = document.querySelector('script[src="https://checkout.razorpay.com/v1/checkout.js"]');
            if (existingScript) {
                console.log('⏳ Razorpay script already being loaded, waiting...');
                existingScript.onload = () => {
                    console.log('✅ Existing Razorpay script loaded');
                    resolve(true);
                };
                existingScript.onerror = () => {
                    console.error('❌ Existing Razorpay script failed to load');
                    resolve(false);
                };
                return;
            }

            console.log('📦 Loading Razorpay script...');
            const script = document.createElement('script');
            script.src = 'https://checkout.razorpay.com/v1/checkout.js';
            script.async = true;
            
            script.onload = () => {
                console.log('✅ Razorpay script loaded successfully');
                // Wait a bit for Razorpay to be available
                setTimeout(() => {
                    if (window.Razorpay) {
                        console.log('✅ Razorpay is now available');
                        resolve(true);
                    } else {
                        console.error('❌ Razorpay script loaded but Razorpay object not available');
                        resolve(false);
                    }
                }, 100);
            };
            
            script.onerror = () => {
                console.error('❌ Failed to load Razorpay script');
                resolve(false);
            };
            
            document.head.appendChild(script);
        });
    };

    // Save bookings and return them for payment
    const saveBookingForPayment = async (paymentType = 'ONLINE') => {
        try {
            // Create individual booking requests for each selected room
            const bookingRequests = [];
            
            // Calculate overall total first to ensure consistency
            const overallTotal = calculateTotal();
            
            // Calculate the base contribution of each room type to the total booking
            let totalRoomContribution = 0;
            const roomContributions = {};
            
            Object.entries(bookingData.selectedRooms).forEach(([roomId, roomSelection]) => {
                if (roomSelection.occupancy && roomSelection.count > 0) {
                    const room = availableRooms.find(r => r.id === parseInt(roomId)) || roomCatalog[roomId];
                    if (room) {
                        const nights = Math.ceil((new Date(bookingData.checkOutDate) - new Date(bookingData.checkInDate)) / (1000 * 60 * 60 * 24));
                        const roomPrice = roomSelection.occupancy === 'single' ? room.singleOccupancyPrice : room.doubleOccupancyPrice;
                        const roomContribution = roomPrice * roomSelection.count * nights;
                        roomContributions[roomId] = roomContribution;
                        totalRoomContribution += roomContribution;
                    }
                }
            });
            
            Object.entries(bookingData.selectedRooms).forEach(([roomId, roomSelection]) => {
                if (roomSelection.occupancy && roomSelection.count > 0) {
                    const room = availableRooms.find(r => r.id === parseInt(roomId)) || roomCatalog[roomId];
                    if (!room) {
                        return;
                    }
                    
                    // Calculate this room's proportional share of the overall total
                    const roomContribution = roomContributions[roomId];
                    const proportionalShare = (roomContribution / totalRoomContribution) * overallTotal;
                    const perRoomShare = proportionalShare / roomSelection.count;

                    // Create booking request for each room instance
                    for (let i = 0; i < roomSelection.count; i++) {
                        // eslint-disable-next-line no-unused-vars
                        const nights = Math.ceil((new Date(bookingData.checkOutDate) - new Date(bookingData.checkInDate)) / (1000 * 60 * 60 * 24));
                        
                        const guestCount = roomSelection.occupancy === 'double' ? 2 : 1;
                        
                        const hasBreakfast = !bookingData.withoutBreakfast;
                        const breakfastCount = hasBreakfast ? guestCount : 0;
                        
                        // Calculate extra guests per room (distribute evenly)
                        const totalSelectedRooms = Object.values(bookingData.selectedRooms)
                            .reduce((sum, rs) => rs.occupancy && rs.count > 0 ? sum + rs.count : sum, 0);
                        const extraAdultsPerRoom = Math.floor(bookingData.extraAdults / totalSelectedRooms) + 
                            (i < (bookingData.extraAdults % totalSelectedRooms) ? 1 : 0);
                        const extraChildrenPerRoom = Math.floor(bookingData.extraChildren / totalSelectedRooms) + 
                            (i < (bookingData.extraChildren % totalSelectedRooms) ? 1 : 0);

                        const bookingPayload = {
                            customerName: bookingData.customerName,
                            mobileNumber: bookingData.mobileNumber,
                            email: bookingData.email,
                            checkInDate: formatDateForBackend(bookingData.checkInDate),
                            checkOutDate: formatDateForBackend(bookingData.checkOutDate),
                            roomId: parseInt(roomId),
                            roomName: room.name,
                            occupancyType: roomSelection.occupancy.toUpperCase(),
                            guests: guestCount, // Base occupancy only (1 for single, 2 for double)
                            breakfast: hasBreakfast,
                            breakfastCount: breakfastCount + (hasBreakfast ? extraAdultsPerRoom + extraChildrenPerRoom : 0),
                            extraBed: extraAdultsPerRoom + extraChildrenPerRoom > 0,
                            extraBedCount: extraAdultsPerRoom + extraChildrenPerRoom,
                            totalPrice: perRoomShare,
                            paymentMethod: paymentType,
                            skipNotifications: true,
                            guestName: bookingData.customerName,
                            guestEmail: bookingData.email,
                            guestPhone: bookingData.mobileNumber
                        };

                        console.log(`🏨 Creating booking for room ${room.name}:`, {
                            roomId: parseInt(roomId),
                            occupancy: roomSelection.occupancy,
                            baseGuests: guestCount,
                            extraAdults: extraAdultsPerRoom,
                            extraChildren: extraChildrenPerRoom,
                            totalExtraGuests: extraAdultsPerRoom + extraChildrenPerRoom,
                            extraBed: extraAdultsPerRoom + extraChildrenPerRoom > 0,
                            extraBedCount: extraAdultsPerRoom + extraChildrenPerRoom
                        });

                        bookingRequests.push(bookingPayload);
                    }
                }
            });

            // Send each booking request
            const bookingPromises = bookingRequests.map(async (bookingPayload) => {
                const headers = {
                    'Content-Type': 'application/json',
                };
                
                // Add JWT token if available
                if (authToken) {
                    headers['Authorization'] = `Bearer ${authToken}`;
                }
                
                const response = await fetch(getApiUrl('/api/bookings'), {
                    method: 'POST',
                    headers: headers,
                    body: JSON.stringify(bookingPayload),
                });

                if (!response.ok) {
                    const errorData = await response.json();
                    throw new Error(errorData.message || 'Error saving booking');
                }

                return response.json();
            });

            const results = await Promise.all(bookingPromises);
            console.log('✅ Bookings created for payment:', results);
            return results;

        } catch (error) {
            console.error('Error saving bookings for payment:', error);
            throw error;
        }
    };

    // Create combined payment order for multiple bookings
    const createCombinedPaymentOrder = async (bookings) => {
        try {
            const bookingIds = bookings.map(b => b.id || b.bookingId);
            const totalAmount = calculateTotal();
            
            const headers = {
                'Content-Type': 'application/json',
            };
            
            if (authToken) {
                headers['Authorization'] = `Bearer ${authToken}`;
            }
            
            const response = await fetch(getApiUrl('/api/payments/create-order/multiple'), {
                method: 'POST',
                headers: headers,
                body: JSON.stringify({
                    bookingIds: bookingIds,
                    totalAmount: totalAmount
                }),
            });

            if (!response.ok) {
                const errorData = await response.json();
                const errorMessage = errorData.error || errorData.message || 'Failed to create payment order';
                console.error('❌ Combined payment order creation failed:', errorMessage);
                throw new Error(errorMessage);
            }

            const paymentOrder = await response.json();
            console.log('✅ Combined payment order created:', paymentOrder);
            
            return { ...paymentOrder, bookings };
        } catch (error) {
            console.error('Error creating combined payment order:', error);
            throw error;
        }
    };

    // Handle Razorpay payment
    const handleRazorpayPayment = async (paymentOrder) => {
        console.log('💳 Starting Razorpay payment with order:', paymentOrder);
        
        return new Promise((resolve, reject) => {
            const options = {
                key: paymentOrder.keyId,
                amount: paymentOrder.amount,
                currency: paymentOrder.currency,
                name: 'The Container Hotel',
                description: paymentOrder.description || `Payment for ${paymentOrder.totalBookings || 1} booking(s)`,
                order_id: paymentOrder.orderId,
                prefill: {
                    name: paymentOrder.customerName,
                    email: paymentOrder.customerEmail,
                },
                theme: {
                    color: '#2563eb'
                },
                method: {
                    netbanking: true,
                    card: true,
                    upi: true,
                    wallet: true,
                    paylater: true
                },
                handler: async function (response) {
                    try {
                        console.log('💳 Payment successful, verifying with backend...');
                        console.log('Payment response:', response);
                        
                        // Verify payment with backend
                        const verifyResponse = await fetch(getApiUrl('/api/payments/verify'), {
                            method: 'POST',
                            headers: {
                                'Content-Type': 'application/json',
                                'Authorization': `Bearer ${authToken}`
                            },
                            body: JSON.stringify({
                                razorpay_order_id: response.razorpay_order_id,
                                razorpay_payment_id: response.razorpay_payment_id,
                                razorpay_signature: response.razorpay_signature
                            })
                        });

                        if (!verifyResponse.ok) {
                            console.error('❌ Payment verification failed:', verifyResponse.status);
                            let errorMessage = 'Payment verification failed';
                            try {
                                const errorData = await verifyResponse.json();
                                errorMessage = errorData.message || errorData.error || errorMessage;
                            } catch (e) {
                                console.error('Failed to parse verification error response');
                            }
                            alert('Payment verification failed: ' + errorMessage);
                            throw new Error(errorMessage);
                        }

                        const verifyResult = await verifyResponse.json();
                        console.log('✅ Payment verified successfully:', verifyResult);
                        alert('Payment completed successfully!');
                        resolve(verifyResult);
                    } catch (error) {
                        console.error('❌ Payment verification error:', error);
                        alert('Payment verification failed: ' + error.message);
                        reject(error);
                    }
                },
                modal: {
                    ondismiss: function() {
                        console.log('❌ Payment modal dismissed by user');
                        // Record cancellation for server-side traceability
                        fetch(getApiUrl('/api/payments/failure'), {
                            method: 'POST',
                            headers: {
                                'Content-Type': 'application/json',
                                'Authorization': `Bearer ${authToken}`
                            },
                            body: JSON.stringify({
                                razorpay_order_id: paymentOrder.orderId,
                                error_code: 'PAYMENT_CANCELLED_BY_USER',
                                error_description: 'Payment was cancelled by user before completion',
                                error_reason: 'Payment modal dismissed'
                            })
                        }).catch(err => console.error('Failed to report payment cancellation to backend:', err));

                        alert('Payment was cancelled. You can retry online or book without payment.');
                        reject(new Error('Payment was cancelled by user'));
                    }
                }
            };

            // Detect test mode based on key
            const isTest = options.key.startsWith('rzp_test_');
            console.log('💳 Razorpay options:', options);
            console.log('🧪 Test mode:', isTest);
            
            // Check if Razorpay is loaded
            if (!window.Razorpay) {
                console.error('❌ Razorpay script not loaded');
                reject(new Error('Razorpay script not loaded'));
                return;
            }

            console.log('🚀 Creating Razorpay instance...');
            const rzp = new window.Razorpay(options);
            
            console.log('✅ Razorpay instance created successfully');
            
            // Set up payment failure handler
            rzp.on('payment.failed', function (response) {
                console.error('❌ Payment failed in Razorpay:', response.error);
                const errorCode = response.error.code;
                const errorDescription = response.error.description;
                const errorReason = response.error.reason;
                
                console.error('Payment failure details:', {
                    code: errorCode,
                    description: errorDescription,
                    reason: errorReason
                });
                
                alert('Payment failed: ' + (errorDescription || errorReason || 'Unknown error'));
                
                // Report failure to backend for logging
                fetch(getApiUrl('/api/payments/failure'), {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                        'Authorization': `Bearer ${authToken}`
                    },
                    body: JSON.stringify({
                        razorpay_order_id: paymentOrder.orderId,
                        error_code: errorCode,
                        error_description: errorDescription,
                        error_reason: errorReason
                    })
                }).catch(err => console.error('Failed to report payment failure to backend:', err));

                reject(new Error(errorDescription || errorReason || 'Payment failed'));
            });

            // Open the Razorpay modal
            console.log('🎯 Opening Razorpay payment modal...');
            try {
                rzp.open();
                console.log('✅ Razorpay modal opened successfully');
            } catch (error) {
                console.error('❌ Failed to open Razorpay modal:', error);
                alert('Failed to open payment modal: ' + error.message);
                reject(new Error('Failed to open payment modal: ' + error.message));
            }
        });
    };

    // Main function to initiate Razorpay payment
    const initiateRazorpayPayment = async (bookings) => {
        try {
            console.log('🚀 Starting Razorpay payment process...');
            console.log('Bookings for payment:', bookings);
            
            // Load Razorpay script
            console.log('📦 Loading Razorpay script...');
            const scriptLoaded = await loadRazorpayScript();
            console.log('📦 Razorpay script loaded:', scriptLoaded);
            
            if (!scriptLoaded) {
                alert('❌ Failed to load payment gateway. Please refresh the page and try again.');
                throw new Error('Failed to load payment gateway. Please try again.');
            }
            
            // Check if Razorpay is available
            if (!window.Razorpay) {
                console.error('❌ Razorpay not found in window object');
                alert('❌ Payment gateway not available. Please refresh the page and try again.');
                throw new Error('Payment gateway not available. Please refresh and try again.');
            }
            
            console.log('✅ Razorpay is available:', typeof window.Razorpay);

            // Create combined payment order for all bookings
            console.log('💳 Creating combined payment order...');
            const paymentOrder = await createCombinedPaymentOrder(bookings);
            console.log('💳 Payment order created:', paymentOrder);

            // Handle Razorpay payment
            console.log('💳 Opening Razorpay payment modal...');
            const paymentResult = await handleRazorpayPayment(paymentOrder);
            console.log('💳 Payment completed:', paymentResult);

            // Send consolidated notification
            await sendConsolidatedNotification(bookings, 'online');

            // Show success page
            const bookingIds = bookings.map(b => b.id || b.bookingId);
            setBookingResult({
                success: true,
                paymentType: 'online_payment',
                bookingIds: bookingIds,
                totalAmount: calculateTotal(),
                guestName: bookingData.customerName,
                guestEmail: bookingData.email,
                guestPhone: bookingData.mobileNumber,
                bookings: bookings
            });
            setRecoverableBookings(null);
            setRecoverablePaymentError('');
            setCurrentStep('success');

        } catch (error) {
            console.error('❌ Razorpay payment process failed:', error);
            // Don't show success page on payment failure
            if (!(error?.message || '').toLowerCase().includes('cancelled')) {
                alert('Payment failed: ' + error.message);
            }
            throw error;
        }
    };

    // Send consolidated notification
    const sendConsolidatedNotification = async (bookings, paymentType) => {
        try {
            console.log('📧 Sending consolidated notification for', bookings.length, 'booking(s)');
            
            // Calculate total amount and guests
            const totalAmount = calculateTotal();
            let totalGuests = 0;
            Object.entries(bookingData.selectedRooms).forEach(([roomId, roomSelection]) => {
                if (roomSelection.occupancy && roomSelection.count > 0) {
                    totalGuests += roomSelection.count * (roomSelection.occupancy === 'double' ? 2 : 1);
                }
            });
            totalGuests += bookingData.extraAdults + bookingData.extraChildren;
            
            const consolidatedNotificationPayload = {
                phoneNumber: bookingData.mobileNumber,
                guestName: bookingData.customerName,
                guestEmail: bookingData.email,
                totalAmount: totalAmount.toString(),
                totalGuests: totalGuests,
                paymentMethod: paymentType === 'online' ? 'online' : 'pay-at-hotel',
                bookings: bookings.map(booking => ({
                    bookingId: booking.id || booking.bookingId,
                    roomName: booking.roomName || 'Room',
                    checkInDate: formatDateForBackend(bookingData.checkInDate),
                    checkOutDate: formatDateForBackend(bookingData.checkOutDate),
                    roomAmount: (booking.totalPrice || 0).toString(),
                    guests: booking.guests || 1,
                    breakfast: booking.breakfast || false,
                    breakfastCount: booking.breakfastCount || 0,
                    extraBed: booking.extraBed || false,
                    extraBedCount: booking.extraBedCount || 0,
                    occupancyType: booking.occupancyType || 'SINGLE'
                }))
            };
            
            const headers = {
                'Content-Type': 'application/json',
            };
            
            if (authToken) {
                headers['Authorization'] = `Bearer ${authToken}`;
            }
            
            const notificationResponse = await fetch('/api/notifications/consolidated', {
                method: 'POST',
                headers: headers,
                body: JSON.stringify(consolidatedNotificationPayload),
            });
            
            if (notificationResponse.ok) {
                console.log('✅ Consolidated notification sent successfully');
            } else {
                console.warn('⚠️ Failed to send consolidated notification, but payment was successful');
            }
        } catch (notificationError) {
            console.error('❌ Error sending consolidated notification:', notificationError);
            // Don't fail the entire process if notification fails
        }
    };

    // Helper function to format date for backend
    const formatDateForBackend = (dateString) => {
        if (!dateString) return '';
        const date = new Date(dateString);
        return date.toISOString().split('T')[0]; // Convert to YYYY-MM-DD format
    };

    // Helper function to format date for display (dd/mm/yyyy)
    const formatDateForDisplay = (dateString) => {
        if (!dateString) return '';
        const date = new Date(dateString);
        const day = String(date.getDate()).padStart(2, '0');
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const year = date.getFullYear();
        return `${day}/${month}/${year}`;
    };

    return (
        <div className="book-rooms-overlay">
            <div className="book-rooms-modal">
                <div className="modal-header">
                    
                </div>
                
                <div className="modal-body">
                    {currentStep === 'booking' && (
                        <div className="booking-form">
                            <h3>Book Your Rooms</h3>
                            <button className="close-btn" color="black" onClick={onClose}>×</button>
                            {/* Guest Details */}
                            <div className="guest-details">
                                <h4>Guest Details</h4>
                                <div className="form-field">
                                    <label htmlFor="customerName">Full Name *</label>
                                    <input
                                        id="customerName"
                                        type="text"
                                        name="customerName"
                                        placeholder="Enter your full name"
                                        value={bookingData.customerName}
                                        onChange={handleInputChange}
                                        className={errors.customerName ? 'error' : ''}
                                    />
                                    {errors.customerName && <span className="error-message">{errors.customerName}</span>}
                                </div>
                                
                                <div className="form-field">
                                    <label htmlFor="mobileNumber">Mobile Number *</label>
                                    <input
                                        id="mobileNumber"
                                        type="tel"
                                        name="mobileNumber"
                                        placeholder="Enter 10-digit mobile number"
                                        value={bookingData.mobileNumber}
                                        onChange={handleInputChange}
                                        className={errors.mobileNumber ? 'error' : ''}
                                        maxLength="10"
                                    />
                                    {errors.mobileNumber && <span className="error-message">{errors.mobileNumber}</span>}
                                </div>
                                
                                <div className="form-field">
                                    <label htmlFor="email">Email Address *</label>
                                    <input
                                        id="email"
                                        type="email"
                                        name="email"
                                        placeholder="Enter your email address"
                                        value={bookingData.email}
                                        onChange={handleInputChange}
                                        className={errors.email ? 'error' : ''}
                                    />
                                    {errors.email && <span className="error-message">{errors.email}</span>}
                                </div>
                            </div>

                            {/* Date Selection */}
                            <div className="date-selection">
                                <h4>Stay Dates</h4>
                                <div className="form-field">
                                    <label htmlFor="checkInDate">Check-in Date *</label>
                                    <input
                                        id="checkInDate"
                                        type="date"
                                        name="checkInDate"
                                        value={bookingData.checkInDate}
                                        onChange={handleInputChange}
                                        className={errors.checkInDate ? 'error' : ''}
                                        min={new Date().toISOString().split('T')[0]}
                                    />
                                    {errors.checkInDate && <span className="error-message">{errors.checkInDate}</span>}
                                </div>
                                
                                <div className="form-field">
                                    <label htmlFor="checkOutDate">Check-out Date *</label>
                                    <input
                                        id="checkOutDate"
                                        type="date"
                                        name="checkOutDate"
                                        value={bookingData.checkOutDate}
                                        onChange={handleInputChange}
                                        className={errors.checkOutDate ? 'error' : ''}
                                        min={bookingData.checkInDate || new Date().toISOString().split('T')[0]}
                                    />
                                    {errors.checkOutDate && <span className="error-message">{errors.checkOutDate}</span>}
                                </div>
                            </div>

                            {/* Room Selection */}
                            <div className="room-selection">
                                <h4>Select Rooms</h4>
                                {errors.rooms && <span className="error-message">{errors.rooms}</span>}
                                
                                {(!bookingData.checkInDate || !bookingData.checkOutDate) && (
                                    <div className="date-reminder">
                                        <span className="info-icon">ℹ️</span>
                                        <span>Please select check-in and check-out dates first to see accurate room availability.</span>
                                    </div>
                                )}
                                
                                {loading ? (
                                    <div className="loading-rooms">
                                        <div className="loading-spinner">🔄</div>
                                        <div>
                                            {bookingData.checkInDate && bookingData.checkOutDate ? 
                                                `Checking room availability for ${formatDateForDisplay(bookingData.checkInDate)} - ${formatDateForDisplay(bookingData.checkOutDate)}...` :
                                                'Loading rooms...'
                                            }
                                        </div>
                                    </div>
                                ) : (
                                    (selectedRoomFilter
                                        ? getRoomsForSelection().filter(room => room.id === selectedRoomFilter.id)
                                        : getRoomsForSelection()
                                    ).map((room, index, displayedRooms) => (
                                        <div key={room.id}>
                                            <div className="room-type-section">
                                                <div className="room-header-with-image">
                                                    <div className="room-image-small">
                                                        <img 
                                                            src={
                                                                (room.imageUrl && !room.imageUrl.includes('/api/placeholder/')) ? room.imageUrl :
                                                                (room.image && !room.image.includes('/api/placeholder/')) ? room.image :
                                                                'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMTAwIiBoZWlnaHQ9IjgwIiB2aWV3Qm94PSIwIDAgMTAwIDgwIiBmaWxsPSJub25lIiB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciPgo8cmVjdCB3aWR0aD0iMTAwIiBoZWlnaHQ9IjgwIiBmaWxsPSIjRTJFOEYwIi8+Cjx0ZXh0IHg9IjUwIiB5PSI0MCIgdGV4dC1hbmNob3I9Im1pZGRsZSIgZm9udC1mYW1pbHk9IkFyaWFsIiBmb250LXNpemU9IjEwIiBmaWxsPSIjNjQ3NDhCIj5Sb29tPC90ZXh0Pgo8L3N2Zz4K'
                                                            } 
                                                            alt={room.name}
                                                            onError={(e) => {
                                                                e.target.src = 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMTAwIiBoZWlnaHQ9IjgwIiB2aWV3Qm94PSIwIDAgMTAwIDgwIiBmaWxsPSJub25lIiB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciPgo8cmVjdCB3aWR0aD0iMTAwIiBoZWlnaHQ9IjgwIiBmaWxsPSIjRTJFOEYwIi8+Cjx0ZXh0IHg9IjUwIiB5PSI0MCIgdGV4dC1hbmNob3I9Im1pZGRsZSIgZm9udC1mYW1pbHk9IkFyaWFsIiBmb250LXNpemU9IjEwIiBmaWxsPSIjNjQ3NDhCIj5Sb29tPC90ZXh0Pgo8L3N2Zz4K';
                                                            }}
                                                        />
                                                    </div>
                                                    <div className="room-info-text">
                                                        <h5>{room.name} 
                                                            {room.isDynamicPricing}
                                                        </h5>
                                                        <p className="room-details">Only {room.availableRooms} Left</p>
                                                        {room.__preservedSelection && (
                                                            <p className="pricing-note">Selected earlier, currently unavailable for selected dates.</p>
                                                        )}
                                                        {room.isDynamicPricing && room.dynamicPricingNote && (
                                                            <p className="pricing-note">Note: {room.dynamicPricingNote}</p>
                                                        )}
                                                    </div>
                                                </div>
                                                
                                                {/* Occupancy Selection - Radio buttons */}
                                                <div className="occupancy-selection">
                                                    <label>Occupancy:</label>
                                                    <div className="radio-group">
                                                        <label className="radio-option">
                                                            <input
                                                                type="radio"
                                                                name={`room-${room.id}-occupancy`}
                                                                checked={bookingData.selectedRooms[room.id]?.occupancy === 'single'}
                                                                onClick={() => handleRoomOccupancyChange(room.id, 'single')}
                                                                onChange={() => {}}
                                                            />
                                                            <span className="radio-label">
                                                                Single Occupancy (₹{room.singleOccupancyPrice})
                                                                {room.isDynamicPricing}
                                                            </span>
                                                        </label>
                                                        <label className="radio-option">
                                                            <input
                                                                type="radio"
                                                                name={`room-${room.id}-occupancy`}
                                                                checked={bookingData.selectedRooms[room.id]?.occupancy === 'double'}
                                                                onClick={() => handleRoomOccupancyChange(room.id, 'double')}
                                                                onChange={() => {}}
                                                            />
                                                            <span className="radio-label">
                                                                Double Occupancy (₹{room.doubleOccupancyPrice})
                                                                {room.isDynamicPricing}
                                                            </span>
                                                        </label>
                                                    </div>
                                                </div>
                                                
                                                {/* Room Count - Select dropdown 1-available */}
                                                <div className="room-count">
                                                    <label>Number of Rooms :</label>
                                                    <select
                                                        value={bookingData.selectedRooms[room.id]?.count || 1}
                                                        onChange={(e) => handleRoomCountChange(room.id, e.target.value)}
                                                        disabled={room.availableRooms === 0}
                                                    >
                                                        {Array.from(
                                                            {
                                                                length: room.availableRooms > 0
                                                                    ? Math.min(5, room.availableRooms)
                                                                    : (bookingData.selectedRooms[room.id]?.count || 1)
                                                            },
                                                            (_, i) => i + 1
                                                        ).map(num => (
                                                            <option key={num} value={num}>{num}</option>
                                                        ))}
                                                    </select>
                                                    {room.availableRooms === 0 && (
                                                        <span className="no-rooms-available">No rooms available</span>
                                                    )}
                                                </div>
                                            </div>
                                            
                                            {/* Add separator line between room types (except for the last room) */}
                                            {index < displayedRooms.length - 1 && (
                                                <div className="room-type-separator"></div>
                                            )}
                                        </div>
                                    ))
                                )}
                            </div>

                            {/* Extra Guests */}
                            <div className="extra-guests">
                                <h4>Additional Guests</h4>
                                
                                <div className="form-row">
                                    <label>Extra Adults : </label>
                                    <select
                                        value={bookingData.extraAdults}
                                        onChange={(e) => handleExtraCountChange('extraAdults', e.target.value)}
                                    >
                                        {[0,1, 2, 3, 4, 5].map(num => (
                                            <option key={num} value={num}>{num}</option>
                                        ))}
                                    </select>
                                </div>
                                
                                <div className="form-row">
                                    <label>Extra Children (Age Above 8 years):</label>
                                    <select
                                        value={bookingData.extraChildren}
                                        onChange={(e) => handleExtraCountChange('extraChildren', e.target.value)}
                                    >
                                        {[0, 1, 2, 3, 4, 5].map(num => (
                                            <option key={num} value={num}>{num}</option>
                                        ))}
                                    </select>
                                </div>
                                
                                <div className="form-row">
                                    <label className="checkbox-option">
                                        <input
                                            type="checkbox"
                                            name="withoutBreakfast"
                                            checked={bookingData.withoutBreakfast}
                                            onChange={handleInputChange}
                                        />
                                        <span className="checkbox-label">Rooms without breakfast</span>
                                    </label>
                                </div>
                            </div>

                            <button className="book-now-btn" onClick={handleBookNow}>
                                Book Now
                            </button>
                        </div>
                    )}

                    {/* OTP Step - Temporarily disabled for DLT registration period */}
                    {currentStep === 'otp' && (
                        <div className="otp-form">
                            <h3>Phone Verification (Temporarily Disabled)</h3>
                            <p>OTP verification is temporarily disabled during DLT registration</p>
                            <div className="otp-actions">
                                <button onClick={() => setCurrentStep('bill')}>Continue to Booking</button>
                            </div>
                            <div className="back-action">
                                <button className="back-btn" onClick={handleGoBackToBooking}>Back to Booking</button>
                            </div>
                        </div>
                    )}

                    {currentStep === 'bill' && (
                        <div className="bill-summary">
                            <h3>Booking Summary</h3>
                            {recoverablePaymentError && (
                                <div className="date-reminder" style={{ marginBottom: '12px' }}>
                                    <span className="info-icon">⚠️</span>
                                    <span>{recoverablePaymentError}</span>
                                </div>
                            )}
                            
                            <div className="booking-details-card">
                                <h4>Guest Information</h4>
                                <div className="booking-detail-item">
                                    <span className="booking-detail-label">Name:</span>
                                    <span className="booking-detail-value">{bookingData.customerName}</span>
                                </div>
                                <div className="booking-detail-item">
                                    <span className="booking-detail-label">Mobile:</span>
                                    <span className="booking-detail-value">{bookingData.mobileNumber}</span>
                                </div>
                                <div className="booking-detail-item">
                                    <span className="booking-detail-label">Email:</span>
                                    <span className="booking-detail-value">{bookingData.email}</span>
                                </div>
                            </div>

                            <div className="booking-details-card">
                                <h4>Stay Information</h4>
                                <div className="booking-detail-item">
                                    <span className="booking-detail-label">Check-in:</span>
                                    <span className="booking-detail-value">{formatDateForDisplay(bookingData.checkInDate)}</span>
                                </div>
                                <div className="booking-detail-item">
                                    <span className="booking-detail-label">Check-out:</span>
                                    <span className="booking-detail-value">{formatDateForDisplay(bookingData.checkOutDate)}</span>
                                </div>
                                <div className="booking-detail-item">
                                    <span className="booking-detail-label">Duration:</span>
                                    <span className="booking-detail-value">
                                        {(() => {
                                            if (bookingData.checkInDate && bookingData.checkOutDate) {
                                                const checkIn = new Date(bookingData.checkInDate);
                                                const checkOut = new Date(bookingData.checkOutDate);
                                                const nights = Math.ceil((checkOut - checkIn) / (1000 * 60 * 60 * 24));
                                                return `${nights} night${nights > 1 ? 's' : ''}`;
                                            }
                                            return 'N/A';
                                        })()}
                                    </span>
                                </div>
                            </div>

                            <div className="booking-details-card">
                                <h4>Room Details</h4>
                                {Object.entries(bookingData.selectedRooms).map(([roomId, roomSelection]) => {
                                    if (roomSelection.occupancy && roomSelection.count > 0) {
                                        const room = availableRooms.find(r => r.id === parseInt(roomId)) || roomCatalog[roomId];
                                        const roomName = room?.name || `Room #${roomId}`;
                                        const currentlyUnavailable = !availableRooms.some(r => r.id === parseInt(roomId));
                                        return (
                                            <div key={roomId} className="booking-detail-item">
                                                <span className="booking-detail-label">
                                                    {roomName} - {roomSelection.occupancy.charAt(0).toUpperCase() + roomSelection.occupancy.slice(1)} × {roomSelection.count}
                                                    {currentlyUnavailable ? ' (currently unavailable for selected dates)' : ''}
                                                </span>
                                            </div>
                                        );
                                    }
                                    return null;
                                })}
                                
                                {(bookingData.extraAdults > 0 || bookingData.extraChildren > 0) && (
                                    <>
                                        {bookingData.extraAdults > 0 && (
                                            <div className="booking-detail-item">
                                                <span className="booking-detail-label">Extra Adults × {bookingData.extraAdults}</span>
                                            </div>
                                        )}
                                        
                                        {bookingData.extraChildren > 0 && (
                                            <div className="booking-detail-item">
                                                <span className="booking-detail-label">Extra Children × {bookingData.extraChildren}</span>
                                            </div>
                                        )}
                                    </>
                                )}
                                
                                <div className="booking-detail-item">
                                    <span className="booking-detail-label">
                                        {bookingData.withoutBreakfast ? 'Rooms Only' : 'Rooms with Complimentary Breakfast'}
                                    </span>
                                </div>
                            </div> 

                            <div className="total-amount-highlight">
                                {(() => {
                                    const breakdown = getCalculationBreakdown();
                                    return (
                                        <>
                                            <div className="subtotal-item">
                                                <span className="subtotal-label">Subtotal:</span>
                                                <span className="subtotal-value">₹{breakdown.subtotal}</span>
                                            </div>
                                            <div className="gst-item">
                                                <span className="gst-label">GST (5%):</span>
                                                <span className="gst-value">₹{breakdown.gstAmount}</span>
                                            </div>
                                            <div className="total-divider"></div>
                                            <h4>Total Amount: ₹{breakdown.total}</h4>
                                        </>
                                    );
                                })()}
                            </div>

                            <div className="bill-actions">
                                <div className="payment-options">
                                    {/* <button onClick={handleBookWithoutPayment} className="book-without-payment-btn" disabled={isSubmitting}>
                                        Book without payment
                                    </button> */}
                                    <button onClick={handlePayOnline} className="pay-online-btn" disabled={isSubmitting}>
                                        Pay online
                                    </button>
                                </div>
                                <div className="back-action">
                                    <button className="back-btn" onClick={handleGoBackToBooking}>Back to Booking</button>
                                </div>
                            </div>
                        </div>
                    )}

                    {currentStep === 'success' && bookingResult && (
                        <div className="success-step">
                            <div className="success-header">
                                <h3>🎉 Rooms Reserved!</h3>
                                <button className="close-btn" onClick={handleCloseSuccess}>×</button>
                            </div>
                            
                            <div className="success-content">
                                <div className="success-icon">
                                    <div className="checkmark">✓</div>
                                </div>
                                
                                <div className="booking-summary">
                                    <h4>Booking Details</h4>
                                    <div className="success-detail">
                                        <span className="label">Guest Name:</span>
                                        <span className="value">{bookingResult.guestName}</span>
                                    </div>
                                    <div className="success-detail">
                                        <span className="label">Email:</span>
                                        <span className="value">{bookingResult.guestEmail}</span>
                                    </div>
                                    <div className="success-detail">
                                        <span className="label">Phone:</span>
                                        <span className="value">{bookingResult.guestPhone}</span>
                                    </div>
                                    <div className="success-detail">
                                        <span className="label">Booking ID(s):</span>
                                        <span className="value">{bookingResult.bookingIds.join(', ')}</span>
                                    </div>
                                    <div className="success-detail total-breakdown">
                                        <span className="label">Total Amount:</span>
                                        <div className="value total-amount-detailed">
                                            {(() => {
                                                const breakdown = getCalculationBreakdown();
                                                return (
                                                    <div className="breakdown-container">
                                                        <div className="breakdown-line">
                                                            <span className="breakdown-label">Subtotal:</span>
                                                            <span className="breakdown-value">₹{breakdown.subtotal}</span>
                                                        </div>
                                                        <div className="breakdown-line">
                                                            <span className="breakdown-label">GST (5%):</span>
                                                            <span className="breakdown-value">₹{breakdown.gstAmount}</span>
                                                        </div>
                                                        <div className="breakdown-divider"></div>
                                                        <div className="breakdown-line total-line">
                                                            <span className="breakdown-label">Total:</span>
                                                            <span className="breakdown-value">₹{breakdown.total}</span>
                                                        </div>
                                                    </div>
                                                );
                                            })()}
                                        </div>
                                    </div>
                                    <div className="success-detail">
                                        <span className="label">Payment Method:</span>
                                        <span className="value">
                                            {bookingResult.paymentType === 'without_payment' ? 'Pay at Hotel' : 'Online Payment'}
                                        </span>
                                    </div>
                                </div>

                                <div className="confirmation-message">
                                    <p>✅ Your rooms are reserved!</p>
                                    <p>📧 To confirm the same hotel will contact you for some advance payment or else booking will be confirmed, subject to availability.</p>
                                    {bookingResult.paymentType === 'without_payment' ? (
                                        <p></p>
                                    ) : (
                                        <p>💳 Payment confirmation will be processed shortly.</p>
                                    )}
                                </div>

                                <div className="resend-actions">
                                    <h4>Need to resend confirmation?</h4>
                                    <div className="resend-buttons">
                                        <button 
                                            className={`resend-btn sms-btn ${smsResendCount >= 2 ? 'disabled' : ''}`}
                                            onClick={handleResendSMS}
                                            disabled={smsResendCount >= 2}
                                            title={smsResendCount >= 2 ? 'SMS resend limit reached (2/2)' : `SMS resends remaining: ${2 - smsResendCount}/2`}
                                        >
                                            🟢 Resend WhatsApp {smsResendCount >= 2 ? '(Limit Reached)' : `(${smsResendCount}/2)`}
                                        </button>
                                        <button 
                                            className={`resend-btn email-btn ${emailResendCount >= 2 ? 'disabled' : ''}`}
                                            onClick={handleResendEmail}
                                            disabled={emailResendCount >= 2}
                                            title={emailResendCount >= 2 ? 'Email resend limit reached (2/2)' : `Email resends remaining: ${2 - emailResendCount}/2`}
                                        >
                                            📧 Resend Email {emailResendCount >= 2 ? '(Limit Reached)' : `(${emailResendCount}/2)`}
                                        </button>
                                    </div>
                                </div>

                                <div className="success-actions">
                                    <button 
                                        className="close-success-btn" 
                                        onClick={handleCloseSuccess}
                                    >
                                        Close
                                    </button>
                                </div>
                            </div>
                        </div>
                    )}

                    {/* Resend Limit Popup */}
                    {showResendLimitPopup && (
                        <div className="resend-limit-popup-overlay">
                            <div className="resend-limit-popup">
                                <div className="resend-limit-header">
                                    <h3>⚠️ Resend Limit Reached</h3>
                                </div>
                                <div className="resend-limit-content">
                                    <p>{resendLimitMessage}</p>
                                    <div className="hotel-contact-info">
                                        <h4>Hotel Contact Information:</h4>
                                        <p><strong>📞 Phone:</strong> +917796775959</p>
                                        <p><strong>📧 Email:</strong> thecontainerkop@gmail.com</p>
                                        <p><strong>🕒 Available:</strong> 24/7 for guest assistance</p>
                                    </div>
                                </div>
                                <div className="resend-limit-actions">
                                    <button 
                                        className="close-resend-limit-btn" 
                                        onClick={() => setShowResendLimitPopup(false)}
                                    >
                                        I Understand
                                    </button>
                                </div>
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default BookRooms;
